import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../utils/cn";
import type { ButtonHTMLAttributes } from "react";

const buttonStyles = cva(
  "inline-flex items-center justify-center rounded-pill font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "bg-grad-accent text-bg hover:opacity-90",
        secondary: "bg-surface text-text hover:bg-surface/80",
        subtle: "border border-muted/30 bg-surface/60 text-text hover:bg-surface/80",
        outline: "border border-muted bg-transparent text-text hover:bg-text/5",
        ghost: "bg-transparent text-text hover:bg-text/10",
        link: "bg-transparent text-accent underline-offset-4 hover:underline",
        success: "bg-success text-bg hover:opacity-90",
        warning: "bg-warning text-bg hover:opacity-90",
        destructive: "bg-danger text-bg hover:opacity-90",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-base",
        lg: "h-12 px-6 text-lg",
      },
      loading: {
        true: "cursor-wait",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const { type = "button", ...rest } = props as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      className={cn(buttonStyles({ variant, size, loading }), className)}
      disabled={disabled || loading}
      type={type}
      {...rest}
    >
      {loading && (
        <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

export default Button;
